"use client";

import { useEffect, useMemo, useState } from "react";
import { AtomicNoResults } from "@coveo/atomic-react";
import { buildSearchBox, type SearchEngine } from "@coveo/headless";
import { RichText } from "@sitecore-content-sdk/nextjs";
import { useTranslations } from "next-intl";

interface CustomNoResultProps {
  engine: SearchEngine;
  resultsNotFoundText?: string;
}

const NO_RESULTS_STYLES = `
  .custom-no-results {
    color: var(--color-dark-gray, #616365);
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    line-height: 22.8571px;
  }
  .custom-no-results p {
    line-height: 22.8571px;
  }
  atomic-no-results::part(icon),
  atomic-no-results::part(search-tips) {
    display: none !important;
  }
  atomic-no-results::part(no-results),
  atomic-no-results::part(cancel-button) {
    margin-right: auto;
  }
  atomic-no-results::part(no-results) {
    color: var(--color-placeholder-text-gray, #67768b);
    font-size: 16px;
  }
  atomic-no-results::part(highlight) {
    color: var(--color-ecolab-blue, #006bd3);
  }
  atomic-no-results::part(cancel-button) {
    font-family: 'Open Sans', sans-serif;
    background-color: transparent;
    font-size: 14px;
    font-weight: normal;
    color: var(--color-coveo-no-result-cancel-cta, #0059b3);
    text-decoration: none;
    cursor: pointer;
    margin-top: 0;
    text-align: left;
    padding: 0;
  }
  atomic-no-results::part(cancel-button):hover,
  atomic-no-results::part(cancel-button):focus {
    text-decoration: underline;
  }
  atomic-no-results::part(ripple) {
    display: none;
  }
  .coveo-richtext p,
  .coveo-richtext ul {
    margin-bottom: 10px;
  }
  .custom-no-result-text::part(no-results) {
    display: none !important;
  }
  @media (max-width: 992px) {
    atomic-no-results::part(no-results),
    atomic-no-results::part(cancel-button) {
      display: none;
    }
  }
  @media (max-width: 1024px) {
    .custom-no-results {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
`;

const CustomNoResult = ({
  engine,
  resultsNotFoundText,
}: CustomNoResultProps) => {
  const t = useTranslations();

  // Build the search-box controller once per engine so the subscription
  // doesn't churn.
  const searchBox = useMemo(() => buildSearchBox(engine), [engine]);
  // Seed the query from the controller's current state via a lazy initializer
  // to avoid a setState-inside-effect, which `react-hooks/set-state-in-effect`
  // flags as a cascading-render risk.
  const [query, setQuery] = useState(() => searchBox.state.value);

  useEffect(() => {
    const unsubscribe = searchBox.subscribe(() => {
      setQuery(searchBox.state.value);
    });
    return () => unsubscribe();
  }, [searchBox]);

  const didYouMeanTemplate = t.has("coveo-no-results-for-did-you-mean")
    ? t("coveo-no-results-for-did-you-mean")
    : 'No results for "{{query}}". Did you mean something else?';
  const didYouMean = didYouMeanTemplate.replaceAll("{{query}}", query);
  const searchTips = t.has("coveo-search-tips") ? t("coveo-search-tips") : "";

  return (
    <AtomicNoResults
      className={`custom-no-results ${resultsNotFoundText ? "custom-no-result-text" : ""}`}
    >
      <style>{NO_RESULTS_STYLES}</style>
      {resultsNotFoundText && <h4>{resultsNotFoundText}</h4>}
      {query && <p className="font-bold mb-[15px] mt-[15px]">{didYouMean}</p>}
      <RichText
        className="coveo-richtext richtext-content text-left"
        field={{ value: searchTips }}
      />
    </AtomicNoResults>
  );
};

export default CustomNoResult;
